import React, { useState } from 'react';
import {
  Box, IconButton, Menu, ToggleButton, ToggleButtonGroup, Tooltip,
} from '@mui/material';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ActionMenuItem, { ACTION_MENU_LIST_PROPS, ACTION_MENU_PAPER_PROPS } from '../../Map/components/ActionMenuItem';
import { listCardActionIconSx } from '../../Map/components/UserLotListsPanel/UserLotListsPanel.styles';
import { recordCardHeaderIconSx } from '../../Map/components/RecordLotIdentity';
import { colors } from '../../styles/theme';
import { META_ANALYSIS_TABS } from '../metaNavigation';
import { campaignLinkedRecords, describeMetaRecord, metaRecordKey } from '../metaCampaigns';
import { formatMetaMoney } from '../metaAdsMoney';
import { formatMetaContacts, metaContactsNote } from '../metaContacts';
import { formatMetaCount } from '../metaFormat';
import MetaAudienceView from './MetaAudienceView';
import { MetaCampaignBudgetDetail } from './MetaBudgetContent';
import MetaInteractionsView from './MetaInteractionsView';
import MetaPlatformsView from './MetaPlatformsView';
import UnitReportDialog from './UnitReportDialog';
import { metaViewToggleSx, metaCampaignSectionsToggleSx } from './metaAdsUi.styles';

/*
 * Detalle de una campaña (paso 2 del refactor UI/UX, solo con fase 1): navegación interna
 * Resultados · Análisis · Presupuesto. Solo la sección elegida está montada, así que Análisis pide
 * sus cortes (con `campaign_id`) recién cuando se abre; Presupuesto no pide nada. El informe ya no es
 * una pestaña (reunión 29/10): es el botón «Informe» de la cabecera (`CampaignReportButton`).
 */
export const META_CAMPAIGN_DETAIL_SECTIONS = [
  { key: 'results', label: 'Resultados' },
  { key: 'analysis', label: 'Análisis' },
  { key: 'budget', label: 'Presupuesto' },
];

const ANALYSIS_VIEWS = { audience: MetaAudienceView, platforms: MetaPlatformsView, interactions: MetaInteractionsView };

/**
 * Grupo «Resultados» del detalle: tal cual `metrics` de `getmetacampaigns` (sin cálculos nuevos).
 * Gasto · Contactos · Costo por contacto · Clics (todos) · Clics en el enlace. Impresiones y Alcance
 * ya están en el grupo «Alcance» del Administrador de anuncios (no se repiten).
 */
export function metaCampaignResultsGroup(metrics, currency) {
  if (!metrics || typeof metrics !== 'object') return null;
  const rows = [];
  const add = (key, label, value, help = null) => { if (value != null && value !== '') rows.push({ key, label, value, help }); };
  add('spend', 'Gasto', metrics.spend != null ? formatMetaMoney(metrics.spend, currency) : null);
  add('contacts', 'Contactos', formatMetaContacts(metrics) || null, metaContactsNote(metrics));
  add('cost_per_contact', 'Costo por contacto', metrics.cost_per_contact != null ? formatMetaMoney(metrics.cost_per_contact, currency) : null);
  add('clicks', 'Clics (todos)', metrics.clicks != null ? formatMetaCount(metrics.clicks) : null);
  add('link_clicks', 'Clics en el enlace', metrics.link_clicks != null ? formatMetaCount(metrics.link_clicks) : null);
  return rows.length ? { key: 'results', title: 'Resultados', rows } : null;
}

function SectionToggle({ options, value, onChange, ariaLabel, testId, sx = null }) {
  return (
    <ToggleButtonGroup
      exclusive
      fullWidth
      size="small"
      value={value}
      onChange={(_e, next) => { if (next) onChange(next); }}
      aria-label={ariaLabel}
      sx={{ ...metaViewToggleSx, ...sx }}
      data-testid={testId}
    >
      {options.map((o) => (
        <ToggleButton key={o.key} value={o.key} data-testid={`${testId}-${o.key}`}>{o.label}</ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
}

/** Análisis de UNA campaña: alcance fijo (no toca el selector global de la subvista Análisis). */
function CampaignAnalysis({ campaign, detail }) {
  const [tab, setTab] = useState(META_ANALYSIS_TABS[0].key);
  const View = ANALYSIS_VIEWS[tab];
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }} data-testid={`meta-campaign-${campaign.id}-analysis`}>
      <SectionToggle
        options={META_ANALYSIS_TABS}
        value={tab}
        onChange={setTab}
        ariaLabel="Análisis de la campaña"
        testId={`meta-campaign-${campaign.id}-analysis-toggle`}
      />
      <View
        campaigns={[{ id: campaign.id, name: campaign.name }]}
        scopeCampaignId={campaign.id}
        period={detail.period}
        ownerUserId={detail.ownerUserId}
        includeInactive={detail.includeInactive}
      />
    </Box>
  );
}

/** Tooltip del botón «Informe» sin unidades vinculadas (el informe es por propiedad). */
export const META_CAMPAIGN_REPORT_NO_UNITS = 'Vinculá la campaña a una unidad para ver el informe';

const reportMenuPaperProps = {
  ...ACTION_MENU_PAPER_PROPS,
  sx: { ...ACTION_MENU_PAPER_PROPS.sx, width: 'auto', minWidth: '164px', maxWidth: 'min(340px, 92vw)' },
};
const reportButtonSx = {
  ...listCardActionIconSx,
  ...recordCardHeaderIconSx,
  // Deshabilitado (sin unidad): gris legible.
  '&.Mui-disabled': { color: colors.disabledOnDarkText },
};
// Área de click de 28 px (t1053u) con el ícono de 18: el margen negativo deja el lugar que ocupa en
// la línea en 18 px (no la agranda ni corre el ícono). En el borde derecho (`edge`), el ícono queda
// alineado con el contenido de abajo.
export const META_CAMPAIGN_REPORT_HIT_PX = 28;
const REPORT_HIT_INSET = (META_CAMPAIGN_REPORT_HIT_PX - 18) / 2;
const reportButtonHitSx = {
  ...reportButtonSx,
  width: META_CAMPAIGN_REPORT_HIT_PX,
  height: META_CAMPAIGN_REPORT_HIT_PX,
  minWidth: META_CAMPAIGN_REPORT_HIT_PX,
  minHeight: META_CAMPAIGN_REPORT_HIT_PX,
  my: `-${REPORT_HIT_INSET}px`,
};

/**
 * Botón «Informe» de la campaña elegida (reunión 29/10, en lugar de la flecha del collapse y de la
 * pestaña «Informe»). El informe es por propiedad: con una unidad vinculada abre su informe
 * (`UnitReportDialog`); con varias, un menú como el ⋮ de Historial para elegir cuál; sin unidad,
 * deshabilitado (gris legible) con el Tooltip que explica.
 */
export const META_CAMPAIGN_REPORT_LABEL = 'Informe de la unidad';

export function CampaignReportButton({ campaign, detail, edge = false }) {
  const [anchor, setAnchor] = useState(null);
  const [open, setOpen] = useState(null);
  const units = campaignLinkedRecords(campaign);
  const disabled = units.length === 0;
  const testId = `meta-campaign-${campaign.id}-report`;
  const handleClick = (e) => {
    if (units.length === 1) setOpen(units[0]);
    else setAnchor(e.currentTarget);
  };
  return (
    <>
      <Tooltip title={<span>{disabled ? META_CAMPAIGN_REPORT_NO_UNITS : META_CAMPAIGN_REPORT_LABEL}</span>} placement="top" arrow describeChild>
        <span style={{ display: 'inline-flex' }} data-testid={`${testId}-tip`}>
          <IconButton
            size="small"
            aria-label={META_CAMPAIGN_REPORT_LABEL}
            aria-haspopup={units.length > 1 ? 'menu' : undefined}
            aria-expanded={anchor ? 'true' : undefined}
            disabled={disabled}
            onClick={handleClick}
            sx={{ ...reportButtonHitSx, ...(edge ? { mr: `-${REPORT_HIT_INSET}px` } : null) }}
            data-testid={testId}
          >
            <DescriptionOutlinedIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </span>
      </Tooltip>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={reportMenuPaperProps}
        MenuListProps={{ ...ACTION_MENU_LIST_PROPS, 'aria-label': 'Informe de la propiedad' }}
      >
        {units.map((rec) => (
          <ActionMenuItem
            key={metaRecordKey(rec)}
            icon={<DescriptionOutlinedIcon sx={{ fontSize: 20 }} />}
            label={describeMetaRecord(rec)}
            testId={`${testId}-unit-${rec.id}`}
            onClick={() => { setAnchor(null); setOpen(rec); }}
          />
        ))}
      </Menu>
      {open ? (
        <UnitReportDialog
          itemKind={open.itemKind}
          recordId={open.id}
          unitLabel={open.label || describeMetaRecord(open)}
          ownerUserId={detail.ownerUserId}
          initialPreset={detail.periodPreset}
          onClose={() => setOpen(null)}
        />
      ) : null}
    </>
  );
}

/**
 * `results` = contenido de hoy de la tarjeta abierta (Administrador de anuncios + unidades +
 * anuncios + enlace a Meta); las demás secciones se montan solo al elegirlas. `write` (F2b): acciones en
 * Meta (`writeActions` + `onAction`) para el ⋮ de cada conjunto en Presupuesto; null = como antes.
 */
export default function MetaCampaignDetail({ campaign, detail, results, write = null }) {
  const [section, setSection] = useState('results');
  return (
    <>
      <SectionToggle
        options={META_CAMPAIGN_DETAIL_SECTIONS}
        value={section}
        onChange={setSection}
        ariaLabel="Detalle de la campaña"
        testId={`meta-campaign-${campaign.id}-sections`}
        // Aire (en el contenido, no en la cabecera sticky): 16 px desde la línea de la cabecera
        // abierta y 12 px hasta el primer título de la sección.
        sx={metaCampaignSectionsToggleSx}
      />
      {section === 'results' ? results : null}
      {section === 'analysis' ? <CampaignAnalysis campaign={campaign} detail={detail} /> : null}
      {section === 'budget' ? <MetaCampaignBudgetDetail campaign={campaign} detail={detail} write={write} /> : null}
    </>
  );
}
